import React, { useEffect, useState } from "react";
import { FileText, Download, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "./ui/dialog";
import { Button } from "./ui/button";
import { formatApiError } from "../context/AuthContext";

export const ManifestV5Panel = ({ client, consolidationId, agent = false }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [pdf, setPdf] = useState(null);
  const id = `manifest-v5-${consolidationId}`;
  const base = `/${agent ? "agent-access" : "admin"}/consolidations/${encodeURIComponent(consolidationId)}/manifest-v5`;
  useEffect(() => {
    if (!open) { setData(null); setError(""); setPdf(null); return; }
    let live = true;
    client.get(`${base}/view`).then(r => { if (live) setData(r.data); })
      .catch(e => { if (live) setError(formatApiError(e)); });
    return () => { live = false; };
  }, [open, client, base]);
  useEffect(() => () => { if (pdf) URL.revokeObjectURL(pdf); }, [pdf]);
  const exportFile = async (kind) => {
    setBusy(true); setError("");
    try {
      const response = await client.get(`${base}/${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      if (kind.endsWith(".pdf")) setPdf(url);
      else {
        const a = document.createElement("a"); a.href = url;
        a.download = `manifest-v5-${consolidationId}.${kind}`; a.click();
        setTimeout(() => URL.revokeObjectURL(url), 30000);
      }
    } catch (e) {
      setError(e.response?.status === 403 ? "Bestaand leesrecht ontbreekt. Er zijn geen rechten toegekend." : formatApiError(e));
    } finally { setBusy(false); }
  };
  const person = (row, side, label) => <div className="min-w-0" data-testid={`${id}-${row.shipping_label_id}-${side}`}>
    <h3 className="text-xs font-bold text-slate-500 mb-1">{label}</h3>
    <p className="font-semibold text-sm [overflow-wrap:anywhere]" data-testid={`${id}-${row.shipping_label_id}-${side}-name`}>{row[side].name || "—"}</p>
    <p className="text-sm [overflow-wrap:anywhere]" data-testid={`${id}-${row.shipping_label_id}-${side}-phone`}>Tel: {row[side].phone || "—"}</p>
    <p className="text-sm [overflow-wrap:anywhere]" data-testid={`${id}-${row.shipping_label_id}-${side}-email`}>E-mail: {row[side].email || "—"}</p>
  </div>;
  return <Dialog open={open} onOpenChange={setOpen}>
    <DialogTrigger asChild><Button variant="outline" size="sm" data-testid={`${id}-open`}><FileText size={14} className="mr-2" />Manifest V5</Button></DialogTrigger>
    <DialogContent className="w-[calc(100vw-2rem)] max-w-4xl max-h-[90dvh] overflow-y-auto" data-testid={`${id}-dialog`}>
      <DialogHeader><DialogTitle data-testid={`${id}-title`}>Manifest V5</DialogTitle>
        <DialogDescription data-testid={`${id}-description`}>{data ? `${data.consolidation.consolidation_number} · ${data.shipments.length} zendingen` : "Contactgegevens"}</DialogDescription>
      </DialogHeader>
      {error && <p role="alert" className="text-sm text-red-700 break-words" data-testid={`${id}-error`}>{error}</p>}
      {!data && !error && <p className="text-sm" data-testid={`${id}-loading`}>Manifest laden…</p>}
      {data && <>
        <div className="flex flex-wrap gap-2" data-testid={`${id}-exports`}>
          <Button size="sm" disabled={busy} onClick={() => exportFile(data.consolidation.transport_mode === "air" ? "air.pdf" : "sea.pdf")} data-testid={`${id}-pdf`}>
            {busy ? <Loader2 size={14} className="animate-spin mr-2" /> : <FileText size={14} className="mr-2" />}PDF / afdrukken</Button>
          {["csv", "xlsx"].map(kind => <Button key={kind} variant="outline" size="sm" disabled={busy} onClick={() => exportFile(kind)} data-testid={`${id}-${kind}`}><Download size={14} className="mr-2" />{kind === "xlsx" ? "Excel" : "CSV"}</Button>)}
        </div>
        {pdf && <iframe title="Manifest V5 PDF" src={pdf} className="w-full h-[60dvh] border rounded" data-testid={`${id}-pdf-preview`} />}
        {!data.shipments.length && <p data-testid={`${id}-empty`}>Geen gekoppelde zendingen.</p>}
        {data.shipments.map(row => <section key={row.shipping_label_id} className="border-t py-4" data-testid={`${id}-shipment-${row.shipping_label_id}`}>
          <div className="flex flex-wrap justify-between gap-2 mb-3">
            <h2 className="text-base font-bold break-all" data-testid={`${id}-number-${row.shipping_label_id}`}>{row.w_number}</h2>
            <p className="text-xs text-slate-500" data-testid={`${id}-metrics-${row.shipping_label_id}`}>{row.boxes} colli · {row.weight_kg} kg · {row.volume_cbm} m³</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{person(row, "sender", "Afzender")}{person(row, "recipient", "Ontvanger")}</div>
        </section>)}
      </>}
    </DialogContent>
  </Dialog>;
};